import type { ConstructorPlan, DoctorOption } from './constructorTypes'

export function ConstructorPlanHeader({
  planDetail,
  doctors,
  totalAmount,
  drawerOpen,
  onClose,
  onPrint,
  onDrawerToggle,
  onStatusChange,
  onDoctorChange,
}: {
  planDetail: ConstructorPlan
  doctors: DoctorOption[]
  totalAmount: number
  drawerOpen: boolean
  onClose: () => void
  onPrint: () => void
  onDrawerToggle: () => void
  onStatusChange: (status: string) => void
  onDoctorChange: (value: string) => void
}) {
  return (
    <div className="sticky top-0 z-10 border-b border-slate-300 bg-white">
      <div className="flex flex-wrap items-start gap-3 px-4 py-2">
        <button
          type="button"
          onClick={onClose}
          className="mt-0.5 inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-100"
          title="Назад к списку планов"
        >
          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          К списку
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-semibold text-slate-900">
            {planDetail.title ?? `План #${planDetail.id}`}
          </h2>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span className="truncate">
              {planDetail.patient_name ? `Пациент: ${planDetail.patient_name}` : `План #${planDetail.id}`}
              {planDetail.created_at ? ` · создан ${new Date(planDetail.created_at).toLocaleDateString('ru-RU')}` : ''}
            </span>
            <label className="flex items-center gap-1.5">
              <span className="text-slate-500">Лечащий врач:</span>
              <select
                className="max-w-[260px] rounded border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-700 focus:border-blue-400 focus:outline-none"
                value={planDetail.doctor_id ?? ''}
                onChange={(event) => onDoctorChange(event.target.value)}
              >
                <option value="">— Не выбран —</option>
                {doctors.map((doctor) => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.display_name}{doctor.role_label ? ` · ${doctor.role_label}` : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <select
          className="rounded border border-slate-300 bg-white px-2 py-1 text-xs focus:border-blue-400 focus:outline-none"
          value={planDetail.status ?? 'active'}
          onChange={(event) => onStatusChange(event.target.value)}
        >
          <option value="active">Активный</option>
          <option value="completed">Завершён</option>
          <option value="archived">Архив</option>
        </select>

        <span className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700">
          Итого: {totalAmount.toLocaleString('ru-RU')} ₽
        </span>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-200 bg-slate-50 px-4 py-1.5 text-xs">
        <span className="inline-flex items-center gap-1.5 rounded border border-slate-200 bg-white px-2.5 py-1 text-slate-600">
          <span className={`h-2 w-2 rounded-full ${planDetail.share?.is_active ? "bg-green-500" : "bg-slate-300"}`} />
          {planDetail.share?.is_active ? "Публичная ссылка активна" : "Шеринг выключен"}
          {planDetail.share?.hide_prices ? " · цены скрыты" : ""}
        </span>
                <button
          type="button"
          className="rounded border border-slate-300 bg-white px-2.5 py-1 font-medium text-slate-700 hover:bg-slate-100"
          onClick={onPrint}
          title="Печать плана"
        >
          Печать
        </button>
        <button
          type="button"
          className="rounded border border-violet-300 bg-violet-50 px-2.5 py-1 font-medium text-violet-700 hover:bg-violet-100"
          onClick={onDrawerToggle}
          title="Открыть аналитику публичного плана"
        >
          Аналитика
          {planDetail.analytics?.view_count ? ` · ${planDetail.analytics.view_count}` : ''}
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onDrawerToggle}
          className={`rounded border px-2.5 py-1 font-medium transition-colors ${
            drawerOpen
              ? 'border-blue-500 bg-blue-500 text-white hover:bg-blue-600'
              : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
          }`}
          title={drawerOpen ? 'Скрыть панель шеринга' : 'Показать панель шеринга'}
        >
          {drawerOpen ? "Скрыть панель шеринга" : "Шеринг плана"}
        </button>
      </div>
    </div>
  )
}
